import { View, Text, StyleSheet } from "react-native";
import React, { useEffect } from "react";

import ClientSummary from "./Summary/ClientSummary";
import ClientPlanification from "./Planification/ClientPlanification";
import ClientCalendar from "./Calendar/ClientCalendar";
import ClientStatistics from "./Statistics/ClientStatistics";
import ClientProfile from "./Profile/ClientProfile";

import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";

import { Ionicons } from "@expo/vector-icons";
import { observer, Observer } from "mobx-react";
import { useScreenDimensions } from "./../_layout";
import ClientForms from "./Forms/ClientForms";

import { useTranslation } from "react-i18next";
import CustomActivityIndicator from "../Components/CustomActivityIndicator";
import SyncStatusIndicator from "../Components/SyncStatusIndicator";
import LogoImage from "../Components/LogoImage";
import { getHeaderLogoMaxWidth } from "../Components/logoImageHeaderSizing";
import GlobalExerciseSearchFloating from "../Exercise/Search/GlobalExerciseSearchFloating";
import UnifiedTabBar from "../Components/UnifiedTabBar";
import { isPendingInvitationClient } from "../invitations/invitedClientUiGuards";
import { NutritionClientEntry } from "../nutrition/NutritionClientEntry";
import { shouldShowNutritionFromSidebarConfig } from "../nutrition/nutritionSidebarVisibility";

const ClientDashboard = observer(
  ({
    client,
    requestProfileLeaveAwareNavigation = null,
    profileDiscardResetVersion = 0,
  }) => {
    const { t } = useTranslation();

    // Check if there are unseen forms (needs to be in observer component for reactivity)
    const hasUnseenForms = client.forms.some((form) => !form.getSeenByCoach);
    const shouldShowNutritionTab = shouldShowNutritionFromSidebarConfig({
      coach: client?.coach,
      sidebarConfig: client?.coach?.sidebarConfig,
    });
    const selectedTab =
      !shouldShowNutritionTab && client.selectedTab === "nutrition-string"
        ? "summary-string"
        : client.selectedTab;

    const TabOptions = shouldShowNutritionTab
      ? [
          "summary-string",
          "planification-string",
          "calendar-string",
          "forms-string",
          "stats-string",
          "nutrition-string",
          "profile-string",
        ]
      : [
          "summary-string",
          "planification-string",
          "calendar-string",
          "forms-string",
          "stats-string",
          "profile-string",
        ];
    const components = {
      "summary-string": ClientSummary,
      "planification-string": ClientPlanification,
      "calendar-string": ClientCalendar,
      "forms-string": ClientForms,
      "stats-string": ClientStatistics,
      "nutrition-string": NutritionClientEntry,
      "profile-string": ClientProfile,
    };
    const ComponentToShow = components[selectedTab] || null;

    const { screenSize } = useScreenDimensions();
    const clientHeaderLogoMaxWidth = getHeaderLogoMaxWidth(screenSize);
    const runWithProfileLeaveGuard = (action) => {
      if (requestProfileLeaveAwareNavigation) {
        requestProfileLeaveAwareNavigation(action);
        return;
      }

      action();
    };

    console.log("clientDashboard refreshed");

    useEffect(() => {
      return () => {
        console.log("ClientDashboard unmounted", client.name);
        if (client?.coach?.selectedChatId != client.id) {
          client.firestoreListener.removeBlocksListener();
          client.syncStatusManager.setOperationStatus("listenerBlocks", null);
        }

        client.firestoreListener.removeFormsListener();
        client.syncStatusManager.setOperationStatus("listenerForms", null);
      };
    }, [client]);

    useEffect(() => {
      if (selectedTab !== client.selectedTab) {
        client.setProperty("selectedTab", selectedTab);
      }
    }, [client, selectedTab]);

    return (
      <>
        {client.isLoading ? (
          <CustomActivityIndicator />
        ) : (
          <View style={[styles.container, { flex: 1 }]}>
            <View style={{ height: "100%" }}>
              {client && (
                <View
                  style={[
                    GlobalStyles.HStack,
                    {
                      alignItems: "center",
                    },
                  ]}
                >
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View
                      style={[
                        GlobalStyles.HStack,
                        {
                          flex: 1,
                          minWidth: 0,
                          justifyContent: "flex-start",
                          alignItems: "center",
                        },
                      ]}
                    >
                      <Observer>
                        {() => (
                          <Text
                            style={[
                              styles.title,
                              {
                                marginRight: screenSize === "small" ? 8 : 16,
                                flexShrink: 1,
                                minWidth: 0,
                              },
                            ]}
                            numberOfLines={1}
                          >
                            {client.nameForCoach}
                          </Text>
                        )}
                      </Observer>
                      <View style={{ flexShrink: 0 }}>
                        <SyncStatusIndicator
                          syncStatusManager={client.syncStatusManager}
                          onPress={() => client.performTransaction()}
                        />
                      </View>
                    </View>
                    {!isPendingInvitationClient(client) ? (
                      <Text
                        style={[
                          styles.subtitle,
                          { fontSize: 14, marginTop: 4, fontWeight: "400" },
                        ]}
                      >
                        {client.email}
                      </Text>
                    ) : (
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          marginTop: 8,
                        }}
                      >
                        <View
                          style={{
                            backgroundColor: "rgba(234, 179, 8, 0.15)",
                            borderRadius: 10,
                            width: 22,
                            height: 22,
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: 8,
                          }}
                        >
                          <Ionicons
                            name="mail-unread-outline"
                            size={12}
                            color="#d97706"
                          />
                        </View>
                        <Text
                          style={[
                            Fonts.regular12,
                            { color: Colors.grayColor, flex: 1 },
                          ]}
                        >
                          {t("pendingInvitationPlanificationBanner-string")}
                        </Text>
                      </View>
                  )}
                </View>
                {client.coach && client.coach.logoUrl && (
                  <View style={{ marginLeft: 16 }}>
                    <LogoImage
                      coach={client.coach}
                      maxWidth={clientHeaderLogoMaxWidth}
                    />
                  </View>
                )}
              </View>
              )}

              <UnifiedTabBar
                tabs={TabOptions.map((option) => ({
                  key: option,
                  label: t(option),
                  showDot: option === "forms-string" && hasUnseenForms,
                }))}
                selectedKey={selectedTab}
                onSelect={(key) =>
                  runWithProfileLeaveGuard(() =>
                    client.setProperty("selectedTab", key),
                  )
                }
                topMargin={30}
                itemGap={screenSize === "small" ? 12 : 24}
              />

              {ComponentToShow &&
                (selectedTab === "profile-string" ? (
                  <ComponentToShow
                    client={client}
                    profileDiscardResetVersion={profileDiscardResetVersion}
                  />
                ) : (
                  <ComponentToShow client={client} />
                ))}
            </View>

            {/* Global floating search - single instance for all exercises and slots */}
            <GlobalExerciseSearchFloating />
          </View>
        )}
      </>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "600",
  },
  subtitle: {
    fontSize: 18,
    fontWeight: "400",
    color: "gray",
  },
});

export default ClientDashboard;
